import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KeyRound, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/auth-context";
import { getSafeAuthReturnUrl } from "@/lib/auth-api";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnUrl?: string;
  onLoginSuccess?: () => void;
};

type GoogleCredentialResponse = {
  credential?: string;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: { theme?: "outline"; size?: "large"; text?: "continue_with"; shape?: "rectangular"; width?: number; locale?: string }) => void;
        };
      };
    };
  }
}

const googleScriptId = "google-identity-services";

function loadGoogleIdentityScript() {
  return new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("Google solo esta disponible en navegador."));
    if (window.google?.accounts?.id) return resolve();

    const existing = document.getElementById(googleScriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("No se pudo cargar Google Identity Services.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = googleScriptId;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("No se pudo cargar Google Identity Services."));
    document.head.appendChild(script);
  });
}

export function AuthDialog({
  open,
  onOpenChange,
  returnUrl,
  onLoginSuccess,
}: Props) {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const googleButtonContainer = useRef<HTMLDivElement>(null);
  const googleInitialized = useRef(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setGoogleLoading(false);
      setShowManual(false);
    }
  }, [open]);

  async function finishLogin(action: () => Promise<unknown>) {
    await action();
    toast.success("Sesión iniciada");

    if (returnUrl) {
      const safeReturnUrl = getSafeAuthReturnUrl(returnUrl);
      await navigate({ to: safeReturnUrl as never });
      onLoginSuccess?.();
      return;
    }

    onOpenChange(false);
    await navigate({ to: "/dashboard" });
  }

  async function handlePasswordLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);

    try {
      await finishLogin(() => login(username, password));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error("Falta configurar VITE_GOOGLE_CLIENT_ID en el front.");
      return;
    }

    let cancelled = false;

    async function renderGoogleButton() {
      setGoogleLoading(true);

      try {
        await loadGoogleIdentityScript();
        if (cancelled) return;

        const googleIdentity = window.google?.accounts?.id;
        if (!googleIdentity) {
          throw new Error("Google Identity Services no esta disponible.");
        }

        if (!googleInitialized.current) {
          googleIdentity.initialize({
            client_id: clientId,
            callback: async (response) => {
              if (!response.credential) {
                toast.error("No se obtuvo el token de Google.");
                return;
              }

              try {
                setGoogleLoading(true);
                await finishLogin(() => loginWithGoogle(response.credential));
              } catch (error) {
                toast.error(error instanceof Error ? error.message : String(error));
              } finally {
                setGoogleLoading(false);
              }
            },
          });
          googleInitialized.current = true;
        }

        if (googleButtonContainer.current) {
          googleIdentity.renderButton(googleButtonContainer.current, {
            theme: "outline",
            size: "large",
            text: "continue_with",
            shape: "rectangular",
            width: 280,
            locale: "es",
          });
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error));
      } finally {
        if (!cancelled) setGoogleLoading(false);
      }
    }

    void renderGoogleButton();

    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <div aria-hidden="true" className="fixed inset-0 z-40 bg-black/80" />
      </DialogPortal>
      <DialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (document.activeElement as HTMLElement | null)?.blur();
        }}
        className="gap-0 overflow-hidden border-0 p-0 sm:max-w-sm"
        style={
          {
            "--background": "oklch(1 0 0)",
            "--foreground": "oklch(0.145 0 0)",
            "--card": "oklch(1 0 0)",
            "--card-foreground": "oklch(0.145 0 0)",
            "--primary": "oklch(0.205 0 0)",
            "--primary-foreground": "oklch(0.985 0 0)",
            "--muted": "oklch(0.97 0 0)",
            "--muted-foreground": "oklch(0.556 0 0)",
            "--accent": "oklch(0.97 0 0)",
            "--accent-foreground": "oklch(0.205 0 0)",
            "--border": "oklch(0.922 0 0)",
            "--input": "oklch(0.922 0 0)",
            "--ring": "oklch(0.708 0 0)",
          } as React.CSSProperties
        }
      >
        <Card className="border-0 shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>Acceder a Rafael González - Osteopatía</DialogTitle>
            <DialogDescription>Inicia sesión para gestionar agenda, pacientes y profesionales.</DialogDescription>
          </DialogHeader>

          <CardHeader className="space-y-2 pb-2 pt-6 text-center">
            <CardTitle className="font-sans text-2xl font-bold tracking-tight">
              Rafael González - Osteopatía
            </CardTitle>
            <p className="text-sm text-muted-foreground">Inicia sesión para continuar</p>
          </CardHeader>

          <CardContent className="space-y-4 px-6 pb-6">
            <div className="flex min-h-10 justify-center pt-4">
              {googleLoading && <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />}
              <div ref={googleButtonContainer} />
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowManual((v) => !v)}
                className="w-full text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                {showManual ? "Ocultar inicio de sesión manual" : "¿Prefieres usar usuario y contraseña?"}
              </button>
            </div>


            {showManual && (
              <form onSubmit={handlePasswordLogin} className="space-y-4 pt-2 border-t border-border/60">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">Usuario o email</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="login-email"
                      autoComplete="username"
                      className="pl-9"
                      placeholder="tu.usuario"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="login-password">Contraseña</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="pl-9"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" variant="outline" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Iniciar sesión
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
